import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface SafeResourceUrlOptions {
  allowedOrigins?: readonly string[];
  allowedDomains?: readonly string[];
  allowAnyHttpsOrigin?: boolean;
  allowBlob?: boolean;
  allowSameOrigin?: boolean;
}

export function validateResourceUrl(
  value: unknown,
  options: SafeResourceUrlOptions = {}
): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const rawUrl = value.trim();

  try {
    const url = new URL(rawUrl, window.location.origin);

    if (url.protocol === 'blob:') {
      return options.allowBlob && url.origin === window.location.origin
        ? url.href
        : null;
    }

    const isSameOrigin = url.origin === window.location.origin;
    const isAllowedOrigin = (options.allowedOrigins || []).includes(url.origin);
    const hostname = url.hostname.toLowerCase();
    const isAllowedDomain = url.protocol === 'https:' && (options.allowedDomains || []).some(domain => {
      const normalizedDomain = domain.trim().toLowerCase().replace(/^\*\./, '');
      return normalizedDomain && (
        hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`)
      );
    });
    const isAllowedHttpsOrigin = options.allowAnyHttpsOrigin && url.protocol === 'https:';
    const isAllowedSameOrigin = options.allowSameOrigin !== false
      && isSameOrigin
      && ['http:', 'https:'].includes(url.protocol);

    if (!isAllowedSameOrigin && !isAllowedOrigin && !isAllowedDomain && !isAllowedHttpsOrigin) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

export function createSafeResourceUrl(
  sanitizer: DomSanitizer,
  value: unknown,
  options: SafeResourceUrlOptions = {}
): SafeResourceUrl | null {
  const validatedUrl = validateResourceUrl(value, options);

  return validatedUrl
    ? sanitizer.bypassSecurityTrustResourceUrl(validatedUrl)
    : null;
}
